import { useMemo } from 'react';
import { ExternalLink, ReceiptText } from 'lucide-react';
import { PageHeader } from '@/components/shared/page-header';
import { EmptyState } from '@/components/shared/empty-state';
import { SearchableSelect } from '@/components/ui/searchable-select';
import { TenantFeesCard } from '@/components/admin/tenant-fees-card';
import { useAdminTenantDetail, useAdminTenants } from '@/hooks/use-admin';
import { apiErrorMessage } from '@/lib/api-error';

interface TenantOption {
  id: string;
  name: string;
  slug?: string;
}

/**
 * Super admin "Fees & Pricing": pick a business, then edit the rates its monthly
 * statements charge. The selected business lives in the URL (#/admin/fees/:tenantId)
 * so a reload or a shared link opens the same rate card.
 */
export function AdminFeesPricingPage({ tenantId = '' }: { tenantId?: string }) {
  // The API caps perPage at 1000; enough to list every business in the picker.
  const tenantsQuery = useAdminTenants({ perPage: 1000 });
  const detailQuery = useAdminTenantDetail(tenantId);

  const tenants = useMemo(() => (tenantsQuery.data?.data ?? []) as TenantOption[], [tenantsQuery.data]);
  const options = useMemo(
    () =>
      [...tenants]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((t) => ({ value: t.id, label: t.slug ? `${t.name} · ${t.slug}` : t.name })),
    [tenants],
  );

  const tenant = detailQuery.data?.data as
    | { name: string; locations?: { name: string; externalId?: string | null }[] }
    | undefined;
  const merchants = useMemo(
    () =>
      (tenant?.locations ?? []).flatMap((loc) =>
        loc.externalId ? [{ merchantId: loc.externalId, name: loc.name }] : [],
      ),
    [tenant],
  );

  const select = (id: string) => {
    window.location.hash = id ? `/admin/fees/${id}` : '/admin/fees';
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Fees & Pricing"
        description="The rates each business's monthly ACH, campaign and POS statements charge, against the platform defaults."
      />

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex w-full max-w-md flex-col gap-1.5">
          <span className="text-sm font-medium text-foreground">Business</span>
          <SearchableSelect
            options={options}
            value={tenantId}
            onChange={select}
            placeholder={tenantsQuery.isLoading ? 'Loading businesses…' : 'Choose a business…'}
            searchPlaceholder="Search businesses…"
            emptyMessage="No matching business"
            disabled={tenantsQuery.isLoading}
          />
        </div>
        {tenantId && tenant && (
          <a
            href={`#/admin/tenants/${tenantId}`}
            className="inline-flex h-9 items-center gap-1.5 rounded-lg px-2 text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Open business
            <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
          </a>
        )}
      </div>

      {tenantsQuery.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn't load businesses: {apiErrorMessage(tenantsQuery.error, 'request failed')}
        </div>
      )}

      {!tenantId ? (
        <div className="rounded-xl border border-border bg-white">
          <EmptyState
            icon={ReceiptText}
            title="Choose a business"
            description="Pick a business above to see and change the rates on its monthly statements."
          />
        </div>
      ) : detailQuery.isLoading ? (
        <div className="rounded-xl border border-border bg-white px-6 py-10 text-center text-sm text-muted-foreground">
          Loading business…
        </div>
      ) : detailQuery.isError || !tenant ? (
        <div className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          Couldn't load this business: {apiErrorMessage(detailQuery.error, 'not found')}
        </div>
      ) : (
        // Keyed by tenant so unsaved edits never carry over to another business.
        <TenantFeesCard key={tenantId} tenantId={tenantId} merchants={merchants} heading={tenant.name} />
      )}
    </div>
  );
}
